import { Controller } from "@hotwired/stimulus"

// Generic AI generation controller: serializes the whole form and posts it.
// Expects:
//   data-controller="ai-generate" somewhere inside a <form>
//   data-ai-generate-url-value="/path" on that element
//   A button with data-action="ai-generate#generate" and data-action-key="some_action_key"
//   A textarea/input with data-ai-generate-target="target" to receive generated text
//   (optional) element with data-ai-generate-target="status" for status/error messages
export default class extends Controller {
  static targets = ["destination", "button", "boolean"]
  static values = { url: String }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  }

  async generate(event) {
    event.preventDefault()

    const btn = event.currentTarget
    const originalContent = btn.innerHTML

    const actionKey = btn.dataset.actionKey || btn.dataset.action_key || btn.dataset.aiGenerateActionValue
    if (!actionKey) { return }

    const form = this.element.closest('form') || this.element.querySelector('form')
    if (!form) { return }

    // Build FormData from entire form so authenticity_token is included automatically.
    const formData = new FormData(form)
    // Remove method override so this remains a plain POST to the magic endpoint
    formData.delete('_method')
    // Remove per-form authenticity token (likely tied to a different action), we'll send a meta-tag token instead
    formData.delete('authenticity_token')
    formData.append('action_key', actionKey)

    try {
      btn.disabled = true
      btn.innerHTML = "Generating…"

      const res = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken || ""
        }, body: formData,
        credentials: 'same-origin'
      })

      const data = await res.json()
      if (!res.ok || !data?.text) {
        throw new Error(data?.error || 'Failed to generate text')
      }

      if (this.hasDestinationTarget) {
        this.destinationTarget.value = data.text
        if (this.hasBooleanTarget) {
          this.booleanTarget.classList.remove('d-none')
          this.booleanTarget.querySelector('input[type="checkbox"]').checked = true
        }
        this.destinationTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.destinationTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }
    } catch (e) {
      alert(e.message)
    } finally {
      btn.disabled = false
      btn.innerHTML = originalContent
    }
  }
}
